import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { C } from "@/brand/tokens";
import { pathways5, pathwayDetails } from "@/brand/content";

/**
 * PathwaysSection5 — Dry Stone chamber.
 *
 * The service architecture is now six clear pathways. No cards, no
 * grid — a disciplined vertical list with brass gutter numerals,
 * hairline separators, and a "learn more" link on each row.
 */
export function PathwaysSection5() {
  return (
    <section
      id="pathways"
      data-testid="pathways"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        {/* Section intro */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                Services
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(2.2rem, 4vw, 3.4rem)",
                fontWeight: 400,
                lineHeight: 1.06,
                letterSpacing: "-0.012em",
              }}
            >
              Four consulting services
              <br />
              for leadership, people,
              <br />
              and culture work.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-6">
            <p
              className="font-inter leading-[1.7] max-w-[44ch]"
              style={{ color: C.inkOnLight, fontSize: "1rem" }}
            >
              Faith 1st is a Leadership, People &amp; Culture Consulting firm.
              We help mission-driven and values-led organizations move
              through high-stakes people moments through senior consulting,
              development, coaching, and workplace investigations — with
              executive judgment in the room.
            </p>
          </div>
        </div>

        {/* Vertical pathway list */}
        <ol
          className="border-t"
          style={{ borderColor: C.borderSoft }}
          data-testid="pathways-list"
        >
          {pathways5.map((p, i) => (
            <li
              key={p.label}
              data-testid={`pathway-${i}`}
              style={{ borderBottom: `1px solid ${C.borderSoft}` }}
            >
              <Link
                to={`/concept-e/pathways/${pathwayDetails[p.n].slug}`}
                className="group grid grid-cols-12 gap-x-6 gap-y-3 py-10 lg:py-12 transition-colors"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(168,138,87,0.05)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {/* Brass numeral gutter */}
                <div className="col-span-2 lg:col-span-1 flex items-start">
                  <span
                    className="font-cormorant tabular-nums"
                    style={{
                      color: C.accentMetal,
                      fontSize: "clamp(1.6rem, 2vw, 1.9rem)",
                      fontWeight: 400,
                      lineHeight: 1,
                    }}
                  >
                    {p.n}
                  </span>
                </div>

                {/* Label */}
                <div className="col-span-10 lg:col-span-4">
                  <h3
                    className="font-cormorant"
                    style={{
                      color: C.textOnLight,
                      fontSize: "clamp(1.4rem, 1.8vw, 1.7rem)",
                      fontWeight: 400,
                      lineHeight: 1.15,
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {p.label}
                  </h3>
                </div>

                {/* Descriptor + bestWhen */}
                <div className="col-span-12 lg:col-span-6">
                  <p
                    className="font-inter leading-[1.65]"
                    style={{
                      color: C.inkOnLight,
                      fontSize: "0.98rem",
                    }}
                  >
                    {p.descriptor}
                  </p>
                  <div
                    className="mt-4 pt-3 flex items-baseline gap-3"
                    style={{ borderTop: `1px solid ${C.paperOver}` }}
                  >
                    <span
                      className="font-inter text-[9.5px] uppercase font-bold shrink-0"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.28em",
                      }}
                    >
                      Best when
                    </span>
                    <p
                      className="font-cormorant italic"
                      style={{
                        color: C.inkOnLight,
                        fontSize: "0.95rem",
                        lineHeight: 1.45,
                      }}
                    >
                      {p.bestWhen}
                    </p>
                  </div>
                </div>

                {/* Arrow marker */}
                <div className="hidden lg:flex col-span-1 justify-end items-start">
                  <ArrowRight
                    size={20}
                    weight="thin"
                    style={{ color: C.accentMetal }}
                    className="group-hover:translate-x-1 transition-transform mt-2"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
